import { DEFAULT_LOCALE } from '@/constants/locales';

/**
 * Template canonical locale: first entry in the tenant supportedLocales list.
 * Multi-locale template selection may replace this later.
 */
export const resolveTemplateSourceLocale = (supportedLocales: string[] | undefined, fallback = DEFAULT_LOCALE): string => {
  if (supportedLocales && supportedLocales.length > 0) {
    return supportedLocales[0];
  }
  return fallback;
};

/** Ordered destination locales for a new landing. */
export const resolveDestinationLocales = (supportedLocales: string[] | undefined, fallback = DEFAULT_LOCALE): string[] => {
  if (supportedLocales && supportedLocales.length > 0) {
    return [...supportedLocales];
  }
  return [fallback];
};

/**
 * Whether a locale-specific value stored under a locale-keyed field contains real content.
 * Payload returns every configured locale key for localized blocks, often with empty default rows.
 */
export const localeValueHasContent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;

  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return value.some((item) => blockRowHasContent(item));
  }

  if (typeof value === 'string') return value.length > 0;

  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if ('root' in o) return true;
    return Object.values(o).some((nested) => localeValueHasContent(nested));
  }

  return true;
};

const blockRowHasContent = (block: unknown): boolean => {
  if (!block || typeof block !== 'object') return false;
  const row = block as Record<string, unknown>;

  if (localeValueHasContent(row.title)) return true;

  const image = row.image;
  if (image && typeof image === 'object') {
    const source = (image as Record<string, unknown>).source;
    if (typeof source === 'string' && source.length > 0) return true;
    if (source && typeof source === 'object') return true;
  }

  if (row.description && typeof row.description === 'object') return true;

  return false;
};

/**
 * Collects locale codes that have non-empty content in locale-keyed objects in the document.
 * Used to know which locales to persist after the initial create (default locale).
 */
export const getLocalesInDocument = (doc: Record<string, unknown>, localeCodes: Set<string>, allowedLocales?: Set<string>): Set<string> => {
  const locales = new Set<string>();
  const walk = (value: unknown): void => {
    if (value === null || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      value.forEach((item) => walk(item));
      return;
    }
    const o = value as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length > 0 && keys.every((k) => localeCodes.has(k))) {
      for (const k of keys) {
        if (allowedLocales && !allowedLocales.has(k)) continue;
        if (localeValueHasContent(o[k])) locales.add(k);
      }
      return;
    }
    for (const v of Object.values(o)) walk(v);
  };
  walk(doc);
  return locales;
};
